import { Component } from "@theme/component";

/** Selectors for controls that should not trigger strip prev/next on `pointerdown`. */
const CARD_SLIDER_STRIP_POINTER_SKIP =
  'a[href], button, input, textarea, select, label[for], [role="button"], [contenteditable="true"], [data-product-card-wishlist]';

/**
 * A center-mode carousel using scale transforms on cards (inactive vs active).
 * Row height for infinite mode is reserved in CSS to limit CLS. The first-load
 * entrance only animates track translateX (not card scale).
 * ≤5 slides: desktop lg+ uses a grid; below lg uses a clone-based infinite track
 * when there are ≥3 slides (same wrap as 6+). With 1–2 slides only, mobile–tablet
 * use a 2-column grid (`data-mobile-grid`). `data-infinite` stays false when ≤5 for CSS.
 * Mouse: 6+ uses midpoint strip (cursors + click); finite 3–5 below lg uses the same
 * **mouseover** slide pick as the desktop grid (`#usesMidpointStripNav`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The slides track container.
 * @property {HTMLElement[]} slides - The slide elements.
 *
 * @extends Component<Refs>
 */
class CardSlider extends Component {
  requiredRefs = ["track", "slides"];

  /** @type {number} - Current slide index (0-based, refers to original slides) */
  #currentIndex = 0;

  /** @type {number} - Uniform slide width in pixels (same for all slides) */
  #slideWidth = 0;

  /** @type {number} - Effective gap between slides (may be negative due to margin) */
  #effectiveGap = 0;

  /** @type {number} - Per-slide margin difference: adjacentMargin − defaultMargin */
  #marginUnit = 0;

  /** @type {number} - Number of adjacent-margin slides before the active slide in the DOM */
  #adjacentCount = 1;

  /** @type {number} - Number of original slides */
  #slideCount = 0;

  /** @type {number} - Number of clones at each end */
  #cloneCount = 0;

  /** @type {boolean} - Whether infinite scroll is enabled */
  #isInfinite = false;

  /** @type {boolean} - Whether currently animating */
  #isTransitioning = false;

  /** @type {boolean} - Desktop mode flag */
  #isDesktop = false;

  /** @type {string | null} - Current cursor zone */
  #cursorZone = null;

  /** @type {boolean} - Dragging state */
  #isDragging = false;

  /** @type {number} - Drag start X position */
  #startX = 0;

  /** @type {number} - Current drag offset */
  #dragOffset = 0;

  /** @type {number} - Track translateX when pointer went down (includes viewport correction) */
  #trackXAtDragStart = 0;

  /** @type {number} - Animation frame ID */
  #animationId = 0;

  /** @type {boolean} - Whether touch moved enough to be considered a drag */
  #hasDragged = false;

  /** @type {number} - Minimum drag to trigger slide change */
  #dragThreshold = 50;

  /** @type {number} - Animation duration in ms */
  #animationDuration = 500;

  /** @type {boolean} - First-load track slide-in (left → center) already played */
  #entranceDone = false;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    delete this.dataset.ready;
    delete this.dataset.mobileGrid;
    window.removeEventListener("resize", this.#handleResize);
    this.#removeEventListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    const { track } = this.refs;
    if (track) {
      track.querySelectorAll("[data-clone]").forEach((clone) => clone.remove());
      track.style.cssText = "";
    }
    window.removeEventListener("resize", this.#handleResize);
    this.#removeEventListeners();
    this.#init();
  }

  #init() {
    const { track, slides } = this.refs;
    if (!track || !slides || slides.length === 0) {
      delete this.dataset.ready;
      return;
    }

    delete this.dataset.ready;

    this.#slideCount = slides.length;
    this.#isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    this.#isInfinite = this.#slideCount > 5;
    this.dataset.infinite = this.#isInfinite;

    this.#measureDimensions();
    this.#syncMobileGridDataset();
    this.#setupTrack();

    if (this.#hasClones()) {
      this.#cloneSlides();
    }

    this.#currentIndex = 0;
    this.#updateSlideStates();

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.#hasClones() && !this.#entranceDone && !prefersReducedMotion) {
      this.#entranceDone = true;
      this.#applyTrackTransform(this.#getTrackXAlignStart(), false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#applyTrackTransform(this.#getTrackXCentered(), true);
        });
      });
    } else {
      if (this.#hasClones()) {
        this.#entranceDone = true;
      }
      this.#setTrackPosition(false);
    }

    this.#addEventListeners();
    window.addEventListener("resize", this.#handleResize);

    this.dataset.ready = "true";
  }

  /**
   * Measure slide width from DOM. All slides have uniform width.
   */
  #measureDimensions() {
    this.#isDesktop = window.innerWidth >= 1024;

    const { slides } = this.refs;
    if (slides && slides.length > 0) {
      this.#slideWidth = slides[0].getBoundingClientRect().width;

      const defaultSlide = slides.length > 2 ? slides[2] : slides[0];
      const defaultMargin = parseFloat(getComputedStyle(defaultSlide).marginRight) || 0;
      this.#effectiveGap = defaultMargin;

      const scale = parseFloat(getComputedStyle(this).getPropertyValue("--card-scale-inactive")) || 0.86;
      this.#marginUnit = (this.#slideWidth * (1 - scale)) / 2;
    }

    // Calculate clone count for infinite scroll (never 0 / NaN — empty clone loops break swipe)
    const containerWidth = this.offsetWidth || window.innerWidth;
    const slideStep = this.#slideWidth + this.#effectiveGap;
    const stepAbs = Math.abs(slideStep) < 1e-6 ? 1 : Math.abs(slideStep);
    const visibleCards = Math.ceil(containerWidth / stepAbs);
    let n = Math.min(Math.ceil(visibleCards / 2) + 2, this.#slideCount);
    if (!Number.isFinite(n) || n < 1) {
      n = Math.min(3, this.#slideCount);
    }
    n = Math.min(this.#slideCount, Math.max(n, 2));
    this.#cloneCount = n;
  }

  /**
   * Clone-based wrap (6+ slides, or below lg with ≥3 slides).
   * @returns {boolean}
   */
  #hasClones() {
    return this.#isInfinite || (!this.#isDesktop && this.#slideCount >= 3);
  }

  /**
   * Midpoint strip click + arrow cursors (infinite / 6+ only). Finite 3–5 below lg
   * uses clones for layout but matches desktop: **mouseover** to pick a slide.
   * @returns {boolean}
   */
  #usesMidpointStripNav() {
    return this.#hasClones() && this.#isInfinite;
  }

  /**
   * 1–2 slides below lg: static grid only (no carousel / clone strip).
   * @returns {boolean}
   */
  #usesMobileFewSlidesGrid() {
    return this.#slideCount < 3 && !this.#isInfinite && !this.#isDesktop;
  }

  /** Sets `data-mobile-grid` for CSS when {@link #usesMobileFewSlidesGrid} is true. */
  #syncMobileGridDataset() {
    if (this.#usesMobileFewSlidesGrid()) {
      this.dataset.mobileGrid = "true";
    } else {
      delete this.dataset.mobileGrid;
    }
  }

  /**
   * Horizontal translateX track: infinite, or finite below lg with ≥3 slides (clones).
   * @returns {boolean}
   */
  #usesTransformTrack() {
    if (this.#isInfinite) return true;
    if (this.#isDesktop) return false;
    return this.#slideCount >= 3;
  }

  #setupTrack() {
    const { track } = this.refs;
    if (!track) return;

    if (this.#hasClones()) {
      track.style.display = "flex";
      track.style.alignItems = "flex-start";
      track.style.willChange = "transform";
    } else if (this.#usesTransformTrack()) {
      track.style.display = "flex";
      track.style.alignItems = "flex-start";
      track.style.willChange = "transform";
    } else {
      track.style.cssText = "";
    }
  }

  /**
   * `cloneNode(true)` copies one-shot Swym sync flags; clones must run their own wishlist UI sync.
   * @param {ParentNode} root
   */
  #resetSwymCardWishlistSyncFlags(root) {
    for (const btn of root.querySelectorAll("[data-product-card-wishlist]")) {
      btn.removeAttribute("data-swym-card-wishlist-synced");
    }
  }

  #cloneSlides() {
    const { track, slides } = this.refs;
    if (!track || !slides) return;

    const originalSlides = Array.from(slides);

    // Clone slides at the end (first N slides)
    for (let i = 0; i < this.#cloneCount; i++) {
      const clone = originalSlides[i].cloneNode(true);
      clone.removeAttribute("ref");
      clone.removeAttribute("data-slide");
      clone.setAttribute("data-clone", "end");
      clone.setAttribute("data-original-index", String(i));
      this.#resetSwymCardWishlistSyncFlags(clone);
      track.appendChild(clone);
    }

    // Clone slides at the beginning (last N slides) - prepend in reverse
    for (let i = 0; i < this.#cloneCount; i++) {
      const sourceIndex = this.#slideCount - 1 - i;
      const clone = originalSlides[sourceIndex].cloneNode(true);
      clone.removeAttribute("ref");
      clone.removeAttribute("data-slide");
      clone.setAttribute("data-clone", "start");
      clone.setAttribute("data-original-index", String(sourceIndex));
      this.#resetSwymCardWishlistSyncFlags(clone);
      track.prepend(clone);
    }
  }

  /**
   * Distance from this element’s client-area origin to the track’s left border,
   * following offsetParent until `this`. `null` if the chain does not reach `this`.
   * @returns {number | null}
   */
  #trackOriginXInSlider() {
    const { track } = this.refs;
    if (!track) return null;

    let x = 0;
    let el = track;
    while (el && el !== this) {
      x += el.offsetLeft;
      el = el.offsetParent;
    }

    return el === this ? x : null;
  }

  /**
   * Horizontal midpoint of the slider’s padded content area, measured from the
   * track’s layout origin (same frame as slide positions). Fixes off-center
   * slides when `card-slider` uses `padding-inline` and `offsetWidth/2` ≠ track coords.
   * @returns {number}
   */
  #contentCenterXFromTrackOrigin() {
    const trackLeft = this.#trackOriginXInSlider();
    if (trackLeft === null) {
      return this.offsetWidth / 2;
    }

    const cs = getComputedStyle(this);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;

    return padL + (this.clientWidth - padL - padR) / 2 - trackLeft;
  }

  /**
   * Left edge of padded content, from the track’s layout origin (entrance align).
   * @returns {number}
   */
  #contentLeftXFromTrackOrigin() {
    const trackLeft = this.#trackOriginXInSlider();
    if (trackLeft === null) {
      return 0;
    }

    const padL = parseFloat(getComputedStyle(this).paddingLeft) || 0;

    return padL - trackLeft;
  }

  /**
   * Horizontal midpoint of padded content in viewport coordinates.
   * @returns {number}
   */
  #viewportContentCenterX() {
    const r = this.getBoundingClientRect();
    const cs = getComputedStyle(this);
    const bl = parseFloat(cs.borderLeftWidth) || 0;
    const br = parseFloat(cs.borderRightWidth) || 0;
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const inner = r.width - bl - br - pl - pr;

    return r.left + bl + pl + inner / 2;
  }

  /**
   * Pixel delta to translate the track so the **original** active slide’s visual
   * midpoint matches the content midpoint (fixes analytical drift from margins,
   * scale, clones, and padding).
   * @param {HTMLElement} track
   * @returns {number}
   */
  #viewportSlideCenterCorrection(track) {
    // Center the slide **slot** we are positioning (original or clone). Using only
    // `[data-slide][data-active]` misses end/start clones when currentIndex is
    // outside 0..slideCount−1 during wrap, so infinite prev/next lands wrong.
    const effectiveIndex = this.#currentIndex + this.#cloneCount;
    const slot = track.children[effectiveIndex];

    if (!slot || !(slot instanceof HTMLElement)) {
      return 0;
    }

    const contentMid = this.#viewportContentCenterX();
    const ar = slot.getBoundingClientRect();
    const slideMid = ar.left + ar.width / 2;

    return contentMid - slideMid;
  }

  /**
   * translateX from layout math only (no drag, no viewport correction).
   * @returns {number}
   */
  #getTrackXCenteredAnalyticalCore() {
    const slideStep = this.#slideWidth + this.#effectiveGap;

    if (!this.#hasClones()) {
      return 0;
    }

    const centerX = this.#contentCenterXFromTrackOrigin();
    const effectiveIndex = this.#currentIndex + this.#cloneCount;
    const slideCenter = effectiveIndex * slideStep + this.#slideWidth / 2 + this.#adjacentCount * this.#marginUnit;

    return centerX - slideCenter;
  }

  /**
   * Current translateX on the track (inline or computed transform).
   * @param {HTMLElement} track
   * @returns {number}
   */
  #readTrackTranslateX(track) {
    const inline = track.style.transform;
    if (inline && inline !== "none") {
      const match = inline.match(/translateX\(([-0-9.e]+)px\)/);
      if (match) {
        return parseFloat(match[1]);
      }
    }

    const t = getComputedStyle(track).transform;
    if (!t || t === "none") {
      return 0;
    }

    try {
      return new DOMMatrixReadOnly(t).m41;
    } catch {
      return 0;
    }
  }

  /**
   * translateX so the current slide is centered (clone-based track).
   * @returns {number}
   */
  #getTrackXCentered() {
    const { track } = this.refs;

    if (this.#isDragging && this.#hasClones() && track) {
      return this.#trackXAtDragStart + this.#dragOffset;
    }

    const base = this.#getTrackXCenteredAnalyticalCore() + this.#dragOffset;

    if (!this.#hasClones() || !track) {
      return base;
    }

    // Measure correction at `base` without leaving the track stuck on `base`:
    // committing `base` here used to jump the strip instantly; applyTrackTransform
    // then only animated the small correction delta.
    const prevTransform = track.style.transform;
    const prevTransition = track.style.transition;
    track.style.transition = "none";
    track.style.transform = `translateX(${base}px)`;
    void track.offsetHeight;
    const correction = this.#viewportSlideCenterCorrection(track);
    track.style.transform = prevTransform;
    track.style.transition = prevTransition;
    void track.offsetHeight;

    return base + correction;
  }

  /**
   * translateX so the current slide’s leading edge aligns with the slider’s
   * leading edge (clone-based track). Used for the first-load entrance.
   * @returns {number}
   */
  #getTrackXAlignStart() {
    if (!this.#hasClones()) {
      return 0;
    }

    const slideStep = this.#slideWidth + this.#effectiveGap;
    const effectiveIndex = this.#currentIndex + this.#cloneCount;
    const slideLeft = effectiveIndex * slideStep + this.#adjacentCount * this.#marginUnit;

    return this.#contentLeftXFromTrackOrigin() - slideLeft + this.#dragOffset;
  }

  /**
   * @param {number} trackX
   * @param {boolean} animate
   */
  #applyTrackTransform(trackX, animate) {
    const { track } = this.refs;
    if (!track) return;

    if (!this.#hasClones()) {
      track.style.transition = "";
      track.style.transform = "";
      return;
    }

    if (animate) {
      track.style.transition = `transform ${this.#animationDuration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`;
      this.#isTransitioning = true;
    } else {
      track.style.transition = "none";
    }

    track.style.transform = `translateX(${trackX}px)`;

    if (animate) {
      setTimeout(() => {
        this.#isTransitioning = false;
        track.style.transition = "";
        this.#checkBoundaryJump();
      }, this.#animationDuration);
    } else {
      this.#checkBoundaryJump();
    }
  }

  /**
   * Apply track position with optional animation.
   * @param {boolean} animate - Whether to animate the transition
   */
  #setTrackPosition(animate = true) {
    const trackX = this.#getTrackXCentered();
    const shouldAnimate = animate && this.#hasClones();
    this.#applyTrackTransform(trackX, shouldAnimate);
  }

  /**
   * After animation, check if we need to jump from clone to real slide.
   */
  #checkBoundaryJump() {
    if (!this.#hasClones()) return;

    const normalized = ((this.#currentIndex % this.#slideCount) + this.#slideCount) % this.#slideCount;

    if (normalized === this.#currentIndex) return;

    this.#currentIndex = normalized;
    this.#updateSlideStates();
    this.#setTrackPosition(false);
  }

  /**
   * Update data-active attribute on all slides (including clones).
   */
  #updateSlideStates() {
    const { track } = this.refs;
    if (!track) return;

    if (this.#usesMobileFewSlidesGrid()) {
      for (const slide of track.querySelectorAll("[data-slide], [data-clone]")) {
        slide.removeAttribute("data-active");
        slide.removeAttribute("data-prev");
        slide.removeAttribute("data-next");
      }
      return;
    }

    const allSlides = track.querySelectorAll("[data-slide], [data-clone]");

    allSlides.forEach((slide) => {
      let slideIndex;

      if (slide.hasAttribute("data-clone")) {
        slideIndex = parseInt(slide.getAttribute("data-original-index") || "0", 10);
      } else {
        slideIndex = parseInt(slide.getAttribute("data-slide") || "0", 10);
      }

      const normalizedCurrent = ((this.#currentIndex % this.#slideCount) + this.#slideCount) % this.#slideCount;
      // Calculate prev and next indices (wrapping around)
      const prevIndex = (((normalizedCurrent - 1) % this.#slideCount) + this.#slideCount) % this.#slideCount;
      const nextIndex = (normalizedCurrent + 1) % this.#slideCount;

      if (slideIndex === normalizedCurrent) {
        slide.setAttribute("data-active", "true");
      } else {
        slide.removeAttribute("data-active");
      }

      // Set data-prev
      if (slideIndex === prevIndex) {
        slide.setAttribute("data-prev", "true");
      } else {
        slide.removeAttribute("data-prev");
      }

      // Set data-next
      if (slideIndex === nextIndex) {
        slide.setAttribute("data-next", "true");
      } else {
        slide.removeAttribute("data-next");
      }
    });
    if (this.#hasClones()) {
      const effectiveIndex = this.#currentIndex + this.#cloneCount;
      const children = track.children;
      let count = 0;
      for (let i = 0; i < effectiveIndex && i < children.length; i++) {
        if (children[i].hasAttribute("data-active") || children[i].hasAttribute("data-prev")) {
          count++;
        }
      }
      this.#adjacentCount = count;
    }
  }

  #addEventListeners() {
    const { track } = this.refs;
    if (!track) return;

    if (!this.#hasClones()) {
      if (this.#isDesktop) {
        track.addEventListener("mouseover", this.#handleSlideHover);
        track.addEventListener("mouseleave", this.#handleSlideHoverEnd);
      }
      if (this.#usesTransformTrack()) {
        track.addEventListener("touchstart", this.#handleTouchStart, { passive: true });
        track.addEventListener("touchmove", this.#handleTouchMove, { passive: false });
        track.addEventListener("touchend", this.#handleTouchEnd);
        track.addEventListener("touchcancel", this.#handleTouchCancel);
      }
      return;
    }

    track.addEventListener("touchstart", this.#handleTouchStart, { passive: true });
    track.addEventListener("touchmove", this.#handleTouchMove, { passive: false });
    track.addEventListener("touchend", this.#handleTouchEnd);
    track.addEventListener("touchcancel", this.#handleTouchCancel);

    if (this.#usesMidpointStripNav()) {
      track.addEventListener("pointerdown", this.#handleTrackPointerDown, { capture: true });
      this.addEventListener("pointermove", this.#handleCursorZone, { capture: true });
      this.addEventListener("pointerleave", this.#handleCursorLeave);
    } else {
      track.addEventListener("mouseover", this.#handleSlideHover);
      track.addEventListener("mouseleave", this.#handleSlideHoverEnd);
    }
  }

  #removeEventListeners() {
    const { track } = this.refs;
    if (!track) return;

    track.removeEventListener("mouseover", this.#handleSlideHover);
    track.removeEventListener("mouseleave", this.#handleSlideHoverEnd);

    track.removeEventListener("touchstart", this.#handleTouchStart);
    track.removeEventListener("touchmove", this.#handleTouchMove);
    track.removeEventListener("touchend", this.#handleTouchEnd);
    track.removeEventListener("touchcancel", this.#handleTouchCancel);

    track.removeEventListener("pointerdown", this.#handleTrackPointerDown, { capture: true });

    this.removeEventListener("pointermove", this.#handleCursorZone, { capture: true });
    this.removeEventListener("pointerleave", this.#handleCursorLeave);

    this.#isDragging = false;
    cancelAnimationFrame(this.#animationId);
  }

  /** @param {MouseEvent} e */
  #handleSlideHover = (e) => {
    const slide = e.target.closest("[data-slide], [data-clone]");
    if (!slide) return;

    let index;
    if (slide.hasAttribute("data-clone")) {
      index = parseInt(slide.getAttribute("data-original-index") || "0", 10);
    } else {
      index = parseInt(slide.getAttribute("data-slide") || "0", 10);
    }

    if (index !== this.#currentIndex) {
      this.#currentIndex = index;
      this.#updateSlideStates();
      if (this.#hasClones()) {
        this.#setTrackPosition(true);
      }
    }
  };

  #handleSlideHoverEnd = () => {};

  /** @param {TouchEvent} e */
  #handleTouchStart = (e) => {
    if (this.#isTransitioning) return;

    const { track } = this.refs;
    if (track && this.#hasClones()) {
      this.#trackXAtDragStart = this.#readTrackTranslateX(track);
    } else {
      this.#trackXAtDragStart = 0;
    }

    this.#isDragging = true;
    this.#hasDragged = false;
    this.#startX = e.touches[0].clientX;
    this.#dragOffset = 0;
    if (this.#usesTransformTrack()) {
      this.#animationId = requestAnimationFrame(this.#animateDrag);
    }
  };

  /** @param {TouchEvent} e */
  #handleTouchMove = (e) => {
    if (!this.#isDragging) return;
    this.#dragOffset = e.touches[0].clientX - this.#startX;

    if (Math.abs(this.#dragOffset) > 10) {
      this.#hasDragged = true;
      e.preventDefault();
    }
  };

  #handleTouchEnd = () => {
    if (!this.#hasDragged) {
      this.#isDragging = false;
      cancelAnimationFrame(this.#animationId);
      this.#dragOffset = 0;
      return;
    }
    this.#finishDrag();
  };

  #handleTouchCancel = () => {
    this.#isDragging = false;
    this.#hasDragged = false;
    this.#dragOffset = 0;
    cancelAnimationFrame(this.#animationId);
  };

  /**
   * Left/right strip prev/next for clone carousels. Uses `pointerdown` and skips
   * `pointerType === "touch"` so mouse/trackpad works below lg without relying
   * on `mousedown` + `#isDesktop` (which blocked narrow viewports entirely).
   *
   * Skips only **interactive** targets (links, buttons, fields), not the whole
   * active slide `<li>` — on 768–1023px the active column is wide; treating the
   * entire card as “no nav” made most clicks do nothing.
   * @param {PointerEvent} e
   */
  #handleTrackPointerDown = (e) => {
    if (this.#isTransitioning) return;
    if (e.button !== 0) return;
    if (e.pointerType === "touch") return;

    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest(CARD_SLIDER_STRIP_POINTER_SKIP)) {
      return;
    }

    const rect = this.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    e.preventDefault();
    if (e.clientX < centerX) {
      this.prev();
    } else {
      this.next();
    }
  };

  /**
   * Match strip `pointerdown` (slider midpoint). Registered as `pointermove`
   * **capture** so this runs before descendants; `mousemove` was unreliable
   * below lg over scaled slides / deep DOM. Skips touch pointers.
   * @param {PointerEvent} e
   */
  #handleCursorZone = (e) => {
    if (e.pointerType === "touch") return;
    if (this.#isDragging) return;

    const rect = this.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    const zone = e.clientX < centerX ? "left" : "right";

    if (zone !== this.#cursorZone) {
      this.#cursorZone = zone;
      this.classList.remove("cursor-prev", "cursor-next");
      this.classList.add(zone === "left" ? "cursor-prev" : "cursor-next");
    }
  };

  #handleCursorLeave = () => {
    this.#cursorZone = null;
    this.classList.remove("cursor-prev", "cursor-next");
  };

  #animateDrag = () => {
    if (this.#isDragging && this.#usesTransformTrack()) {
      this.#setTrackPosition(false);
      requestAnimationFrame(this.#animateDrag);
    }
  };

  #finishDrag() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    cancelAnimationFrame(this.#animationId);

    if (!this.#hasDragged) {
      this.#dragOffset = 0;
      return;
    }

    if (Math.abs(this.#dragOffset) > this.#dragThreshold) {
      if (this.#dragOffset < 0) {
        this.#goToSlide(this.#currentIndex + 1);
      } else {
        this.#goToSlide(this.#currentIndex - 1);
      }
    } else {
      this.#dragOffset = 0;
      this.#setTrackPosition(true);
    }
    this.#hasDragged = false;
  }

  /**
   * Navigate to a specific slide index.
   * @param {number} index - Target slide index
   */
  #goToSlide(index) {
    if (!this.#hasClones()) {
      index = Math.max(0, Math.min(index, this.#slideCount - 1));
    }

    this.#currentIndex = index;
    this.#dragOffset = 0;
    this.#updateSlideStates();
    this.#setTrackPosition(true);
  }

  #handleResize = () => {
    const { track } = this.refs;
    const hadClonesInDom = track?.querySelector("[data-clone]") != null;
    const prevCloneCount = this.#cloneCount;

    this.#measureDimensions();
    this.#syncMobileGridDataset();
    this.#setupTrack();

    const shouldHaveClones = this.#hasClones();
    const cloneCountChanged = this.#cloneCount !== prevCloneCount;
    const listenerModeChanged = hadClonesInDom !== shouldHaveClones;

    if (listenerModeChanged || (shouldHaveClones && hadClonesInDom && cloneCountChanged)) {
      if (track) {
        track.querySelectorAll("[data-clone]").forEach((clone) => clone.remove());
      }
      this.#currentIndex = ((this.#currentIndex % this.#slideCount) + this.#slideCount) % this.#slideCount;
      if (shouldHaveClones) {
        this.#measureDimensions();
        this.#cloneSlides();
      }
    }

    if (listenerModeChanged) {
      this.#removeEventListeners();
      this.#addEventListeners();
    }

    this.#updateSlideStates();
    this.#setTrackPosition(false);
  };

  /**
   * Navigate to previous slide.
   * @param {Event} [e]
   */
  prev(e) {
    e?.preventDefault();
    if (this.#isTransitioning) return;

    if (!this.#hasClones() && this.#currentIndex <= 0) return;

    this.#goToSlide(this.#currentIndex - 1);
  }

  /**
   * Navigate to next slide.
   * @param {Event} [e]
   */
  next(e) {
    e?.preventDefault();
    if (this.#isTransitioning) return;

    if (!this.#hasClones() && this.#currentIndex >= this.#slideCount - 1) return;

    this.#goToSlide(this.#currentIndex + 1);
  }
}

if (!customElements.get("card-slider")) {
  customElements.define("card-slider", CardSlider);
}
